<template>
  <base-page-layout title="Resultados da consulta">
    <mf-button label="Voltar" color="marca-0-l40" icon="arrow_back" icon-position="left" text @click="goBack" />
    <v-card class="margin-bottom mt-2">
      <v-card-text>
        <consumer-header :consumer="consumerData" />
        <div v-if="hasPointsModule" class="d-flex flex-row align-center mt-4">
          <span class="bold-14px color-marca-0-l30 remaining-points-label">Pontos restantes na carteira:</span>
          <span class="bold-16px color-marca-0-l30">{{ localeString(userPoints) }}</span>
        </div>

        <v-divider class="my-8 cinza-l60" />

        <div v-if="activeVoucherData">
          <div class="d-md-flex d-xs-block flex-row align-center mb-2">
            <span class="medium-number-bold-font color-marca-0-l10">Vale-troca ativo:</span>
            <span class="medium-number-bold-font color-marca-0-l30 ml-1">{{ voucherProductName }}</span>
          </div>

          <span class="bold-14px color-cinza-0">Ativado em: </span>
          <span class="bold-14px color-cinza-0"> {{ voucherActivatedAt }} </span>
        </div>

        <div v-else>
          <span class="medium-number-bold-font color-marca-0-l10">Não há nenhum vale-troca ativo</span>
        </div>

        <mf-tabs-menu v-model="tab" :tabs="tabItems" class="mt-8" />
        <div v-if="tab === 0 && activeVoucherData && !activeVoucherData.money" class="mt-8">
          <v-card flat class="pa-2 d-flex flex-column voucher-card">
            <div class="d-flex flex-row align-center pt-4 pl-4 pb-2 voucher-header">
              <img :src="voucherImageUrl" :alt="voucherProductName" class="voucher-img" />
              <span class="bold-14px color-cinza-0"> {{ voucherProductName }} </span>
            </div>

            <v-divider class="my-2 cinza-l80" />

            <div class="d-flex flex-sm-row flex-column justify-space-between align-center px-2 voucher-card-bottom">
              <div class="align-self-start">
                <div class="bold-14px color-cinza-l30">Loja</div>
                <div class="bold-14px color-cinza-d20">{{ voucherStoreName }}</div>
              </div>
              <div class="align-self-start">
                <div class="bold-14px color-cinza-l30">Datas</div>
                <div class="bold-14px color-cinza-0">
                  <span class="bold-14px color-cinza-d10"> Ativado em: </span>
                  {{ voucherActivatedAt }}
                  <span class="bold-14px color-cinza-l30 mx-4 hidden-sm-and-down">|</span>
                  <br class="hidden-md-and-up" />
                  <span class="bold-14px color-cinza-d10"> Expira em: </span>
                  {{ voucherExpiresAt }}
                </div>
              </div>
              <mf-button label="Entregar produto" class="align-content-center text-right" @click="openRescueDialog" />
            </div>
          </v-card>
        </div>

        <div v-else-if="tab === 0" class="my-8">
          <span class="bold-14px color-cinza-0">Não há vale-troca por produto ativo para ser entregue.</span>
        </div>

        <div v-if="tab === 1" class="mt-8">
          <extract-history-table
            :history="extractHistory"
            :current-page="extractHistoryPage"
            :total-pages="extractHistoryTotalPages"
            :loading="loading"
            @page-change="handleExtractPageChange"
            @filter-change="handleExtractFilterChange"
          />
        </div>
      </v-card-text>
    </v-card>
    <rescue-code-dialog
      :value="showRescueDialog"
      :state="rescueDialogState"
      :loading="rescueDialogLoading"
      :document-no="consumer.document_no"
      :unit="activatedUnitId"
      :blocked-until="rescueBlockedUntil"
      @input="showRescueDialog = false"
      @confirm="handleRescueConfirm"
      @error="handleRescueError"
      @update:loading="rescueDialogLoading = $event"
    />
  </base-page-layout>
</template>

<script>
import { DateTime } from 'luxon'
import { localeString } from '@/config/helpers.js'
import BasePageLayout from '@/components/BasePageLayout.vue'

import ConsumerHeader from '../components/ConsumerHeader.vue'
import ExtractHistoryTable from '../components/ExtractHistoryTable.vue'
import RescueCodeDialog from '@/components/RescueCodeDialog.vue'
import { QUERY_USER_POINTS_HISTORY, QUERY_USER_ACTIVE_PRODUCT_VOUCHER } from '../../graphql'

export default {
  components: {
    BasePageLayout,
    ConsumerHeader,
    ExtractHistoryTable,
    RescueCodeDialog,
  },
  props: {
    consumer: {
      type: Object,
      required: true,
    },
    activeVoucher: {
      type: Object,
      required: false,
      default: null,
    },
  },
  data: () => ({
    loading: false,
    tab: 0,
    tabItems: [
      {
        name: 'Entrega de produto',
      },
      {
        name: 'Extrato',
      },
    ],
    showRescueDialog: false,
    rescueDialogState: 'initial',
    rescueDialogLoading: false,
    rescueBlockedUntil: null,
    extractHistory: [],
    extractHistoryPage: 1,
    extractHistoryTotalPages: 1,
    extractHistoryPageSize: 10,
    extractHistoryInitialDate: null,
    extractHistoryFinalDate: null,
    extractHistoryUnits: null,
    consumerData: null,
    activeVoucherData: null,
  }),
  computed: {
    hasPointsModule() {
      return this.$getAllowedModules()['points']
    },
    userPoints() {
      return this.consumerData?.points || 0
    },
    voucherProductName() {
      if (this.activeVoucherData?.money) {
        return `R$ ${this.localeString(this.activeVoucherData.money)} de desconto`
      }
      return this.activeVoucherData?.product_description
    },
    voucherStoreName() {
      return this.activeVoucherData?.activated_unit?.name
    },
    voucherActivatedAt() {
      return DateTime.fromISO(this.activeVoucherData?.activated_at).toFormat('dd/MM/yyyy - HH:mm')
    },
    voucherExpiresAt() {
      return DateTime.fromISO(this.activeVoucherData?.expires_at).toFormat('dd/MM/yyyy - HH:mm')
    },
    voucherImageUrl() {
      return this.activeVoucherData?.image_url
    },
    formattedBlockedUntil() {
      if (!this.rescueBlockedUntil) return ''
      return DateTime.fromISO(this.rescueBlockedUntil).toFormat('HH:mm')
    },
    activatedUnitId() {
      return this.activeVoucherData?.activated_unit?.id_erp || ''
    },
  },
  watch: {
    tab(newTab) {
      if (newTab === 1) {
        this.fetchExtractHistory()
      } else {
        this.fetchUserActiveProductVoucher()
      }
    },
  },
  beforeMount() {
    this.consumerData = this.consumer
    this.activeVoucherData = this.activeVoucher
  },
  mounted() {
    if (typeof this.$props.consumer !== 'object') {
      this.goBack()
    }
  },
  methods: {
    localeString,
    goBack() {
      this.$router.push({ name: 'consultar-pontos' })
    },
    async fetchUserActiveProductVoucher() {
      try {
        const { data } = await this.$apollo.query({
          query: QUERY_USER_ACTIVE_PRODUCT_VOUCHER,
          variables: {
            documentNo: this.consumer.document_no,
          },
          fetchPolicy: 'no-cache',
        })

        const response = data.userActiveProductVoucher
        this.consumerData = response.userInfo
        this.activeVoucherData = response.activeVoucher || null
      } catch (err) {
        console.error('Erro ao buscar dados atualizados:', err)
        this.$snackbar({ message: 'Erro ao buscar dados atualizados', snackbarColor: 'error' })
      }
    },
    mapEventType(type) {
      const map = {
        PURCHASE: 'Compra',
        PURCHASE_CANCELED: 'Compra cancelada',
        POINTS_EXPIRED: 'Pontos expirados',
        POINTS_CAMPAIGN: 'Recebimento de pontos',
        POINTS_READJUSTED: 'Ajuste',
        VOUCHER_PURCHASED: 'Vale-troca ativado',
        VOUCHER_CANCELED: 'Vale-troca cancelado',
        VOUCHER_EXPIRED: 'Vale-troca expirado',
        POINTS_BLOCKED: 'Compra sem pontos',
        VOUCHER_CONSUMED: 'Vale-troca utilizado',
      }
      return map[type] || type
    },
    async fetchExtractHistory(page = 1) {
      this.loading = true
      try {
        let formattedInitialDate = null
        let formattedFinalDate = null

        if (this.extractHistoryInitialDate) {
          formattedInitialDate = DateTime.fromISO(this.extractHistoryInitialDate).toUTC().toISO()
        }

        if (this.extractHistoryFinalDate) {
          formattedFinalDate = DateTime.fromISO(this.extractHistoryFinalDate).toUTC().toISO()
        }

        const { data } = await this.$apollo.query({
          query: QUERY_USER_POINTS_HISTORY,
          variables: {
            document_no: this.consumer.document_no,
            pageSize: this.extractHistoryPageSize,
            page,
            initialDate: formattedInitialDate,
            finalDate: formattedFinalDate,
            units: this.extractHistoryUnits,
          },
          fetchPolicy: 'no-cache',
        })
        const resp = data.userPointsHistory
        this.extractHistoryPage = resp.page
        this.extractHistoryTotalPages = Math.ceil(resp.records / this.extractHistoryPageSize)
        this.extractHistory = resp.items.map((item) => ({
          date: item.date,
          type: this.mapEventType(item.type),
          unit: item.unit?.name,
          total: item.money ? `R$ ${this.localeString(item.money, 2)}` : undefined,
          points: item.points > 0 ? `+ ${this.localeString(item.points)}` : item.points < 0 ? `- ${this.localeString(Math.abs(item.points))}` : undefined,
        }))
      } catch (err) {
        this.extractHistory = []
        this.$snackbar({ message: 'Erro ao buscar extrato de pontos', snackbarColor: 'error' })
      } finally {
        this.loading = false
      }
    },
    openRescueDialog() {
      this.rescueDialogState = 'initial'
      this.showRescueDialog = true
    },
    async handleRescueConfirm() {
      await this.fetchUserActiveProductVoucher()

      this.rescueDialogState = 'success'
      this.$snackbar({ message: 'O vale-troca de produto foi resgatado com sucesso', snackbarColor: 'success' })
      this.tab = 1
      this.showRescueDialog = false
    },
    handleRescueError(error) {
      if (error?.blockedUntil) {
        this.rescueBlockedUntil = error.blockedUntil
        this.rescueDialogState = 'error'
        this.$snackbar({
          message: `Você excedeu o número de tentativas. Tente novamente após ${this.formattedBlockedUntil}.`,
          snackbarColor: 'error',
          timeout: 10000,
        })
      } else {
        this.rescueDialogState = 'error'
      }
    },
    handleExtractPageChange(page) {
      this.fetchExtractHistory(page)
    },
    handleExtractFilterChange({ initialDate, finalDate, units }) {
      this.extractHistoryInitialDate = initialDate
      this.extractHistoryFinalDate = finalDate
      this.extractHistoryUnits = units
      this.fetchExtractHistory()
    },
  },
}
</script>

<style lang="scss" scoped>
.margin-bottom {
  margin-bottom: 74px;
}

.bold-14px {
  font-size: 14px;
  font-weight: 700;
}

.bold-16px {
  font-size: 16px;
  font-weight: 700;
}

.bold-22px {
  font-size: 22px;
  font-weight: 700;
}

.normal-14px {
  font-size: 14px;
  font-weight: 400;
}

.color-marca-0-l10 {
  color: $marca-0-l10;
}

.color-marca-0-l30 {
  color: $marca-0-l30;
}

.color-orange {
  color: #f36600;
}

.color-cinza-0 {
  color: $cinza-0;
}

.color-cinza-l30 {
  color: $cinza-l30;
}

.color-cinza-d20 {
  color: $cinza-d20;
}

.color-cinza-d10 {
  color: $cinza-d10;
}

.remaining-points-label {
  margin-right: 2px;
}

.voucher-card {
  background: #f6f6f8;
  border-radius: 8px;
  border: 1px solid $cinza-l80;

  &-bottom {
    @media (max-width: 600px) {
      gap: 12px;

      ::v-deep .v-btn,
      .v-badge {
        width: 100%;
      }
    }
  }
}

.voucher-img {
  width: 36px;
  height: 36px;
  object-fit: contain;
  margin-right: 16px;
}
</style>
